import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NavBar from '../../components/NavBar';
import Breadcrumbs from '../../components/Breadcrumbs';
import StoreCTA from '../../components/StoreCTA';
import '../../styles/gameInfo.css';

function DailyLogicPuzzlesPage() {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': [
      {
        '@type': 'Question',
        'name': 'When do the daily puzzles reset?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'A fresh set of puzzles arrives each day, so you can return daily for new challenges.'
        }
      },
      {
        '@type': 'Question',
        'name': 'How long does a daily puzzle take?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Most daily puzzles are designed for short sessions, typically a few minutes each.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Do I need an account to play?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'No account is required. You can play instantly on desktop or mobile.'
        }
      }
    ]
  };

  const videoGameSchema = {
      "@context": "https://schema.org",
      "@type": "VideoGame",
      "name": "Daily Logic Puzzles",
      "description": "A fresh set of bite-sized logic puzzles every day. Come back daily for a new challenge.",
      "url": "https://logicpuzzles.ca/games/daily-logic-puzzles",
      "image": "https://logicpuzzles.ca/dailylogicpuzzles/icons/Icon-512.png",
      "genre": "Logic Puzzle",
      "gamePlatform": "Web Browser",
      "operatingSystem": "Any (browser-based)",
      "playMode": "SinglePlayer",
      "inLanguage": "en",
      "isAccessibleForFree": true,
      "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "CAD",
          "availability": "https://schema.org/InStock"
      },
      "publisher": {
          "@id": "https://logicpuzzles.ca/#organization"
      },
      "potentialAction": {
          "@type": "PlayAction",
          "target": "https://logicpuzzles.ca/play/dailylogicpuzzles"
      }
  };

  return (
    <div>
      <Helmet>
        <title>Daily Logic Puzzles - New Brain Teasers Every Day | Logic Puzzles CA</title>
        <meta
          name="description"
          content="Play Daily Logic Puzzles for free! Enjoy a fresh set of bite-sized logic challenges every day. Perfect for quick brain training on desktop and mobile."
        />
        <meta
          name="keywords"
          content="daily logic puzzles, daily puzzles, brain teasers, logic puzzles online, deductive reasoning, free logic games"
        />
        <meta property="og:title" content="Daily Logic Puzzles - New Brain Teasers Every Day" />
        <meta
          property="og:description"
          content="A fresh set of bite-sized logic puzzles every day. Free to play, mobile-friendly, and built for quick brain training."
        />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://logicpuzzles.ca/dailylogicpuzzles/icons/Icon-512.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Daily Logic Puzzles - New Brain Teasers Every Day" />
        <meta
          name="twitter:description"
          content="Play a fresh set of bite-sized logic puzzles every day. Free to play and mobile-friendly."
        />
        <meta name="twitter:image" content="https://logicpuzzles.ca/dailylogicpuzzles/icons/Icon-512.png" />
        <link rel="canonical" href="https://logicpuzzles.ca/games/daily-logic-puzzles" />
        <script type="application/ld+json">{JSON.stringify(videoGameSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <NavBar />
      <Breadcrumbs categoryName="Logic Games" categoryPath="/logic-games" currentLabel="Daily Logic Puzzles" />

      <header className="gi-hero">
        <div className="container gi-hero__inner">
          <div>
            <p className="eyebrow">Daily Puzzles · Free online</p>
            <h1>Daily Logic Puzzles</h1>
            <p className="gi-hero__lead">A fresh set of bite-sized logic challenges every day</p>
            <div className="gi-hero__actions">
              <Link to="/play/dailylogicpuzzles" className="button">▶ Play today’s puzzles</Link>
              <a href="#whats-inside" className="button secondary">What you’ll get</a>
            </div>
          </div>
          <div className="gi-hero__art" aria-hidden="true">
            <img src="/dailylogicpuzzles/icons/Icon-192.png" alt="" width="168" height="168" />
          </div>
        </div>
      </header>

      <div className="gi-main">
        <StoreCTA app="dlp" placement="game-page" page="/games/daily-logic-puzzles" headline="Get Daily Logic Puzzles on your phone" />

        <section className="gi-section">
          <p className="gi-lead">
            <strong>Daily Logic Puzzles</strong> is a lightweight way to keep your brain sharp - quick sessions,
            clear rules, and satisfying “aha!” moments. Come back each day for a new challenge.
          </p>
        </section>

        <section className="gi-section" id="whats-inside">
          <h2>What you’ll get</h2>
          <div className="gi-grid">
            <div className="gi-card">
              <h3>Daily variety</h3>
              <p>Fresh logic puzzles designed for quick play and steady improvement.</p>
            </div>
            <div className="gi-card">
              <h3>Real brain training</h3>
              <p>Practice pattern recognition, constraints, and deductive reasoning.</p>
            </div>
            <div className="gi-card">
              <h3>Mobile-ready</h3>
              <p>Designed to feel great on phone, tablet, and desktop.</p>
            </div>
          </div>
        </section>

        <section className="gi-section">
          <h2>Daily Logic Puzzles FAQ</h2>
          <div className="gi-faq">
            <details open>
              <summary>Are the puzzles all the same type?</summary>
              <p>No. The daily lineup mixes different logic styles so you build a broad set of skills.</p>
            </details>
            <details>
              <summary>Can I replay older puzzles?</summary>
              <p>You can always replay puzzles from the day’s set, and we rotate formats to keep things fresh.</p>
            </details>
            <details>
              <summary>Is it mobile friendly?</summary>
              <p>Yes. The UI is optimized for phones and tablets, with tap-friendly controls.</p>
            </details>
          </div>
        </section>

        <section className="gi-cta">
          <h2>Ready for today’s puzzle?</h2>
          <p>Jump in now - it’s free, and it only takes a few minutes to get hooked.</p>
          <div className="gi-cta__actions">
            <Link to="/play/dailylogicpuzzles" className="button">Play Daily Logic Puzzles</Link>
            <Link to="/logic-games" className="button secondary">Browse All Logic Games</Link>
          </div>
        </section>
      </div>
    </div>
  );
}

export default DailyLogicPuzzlesPage;
